import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./MainPage.css";
// @ts-ignore
import FilterPanel from "../Filter/FilterPanel.tsx";
// @ts-ignore
import TopCharts from "../MainPage/Charts/TopCharts/TopCharts.tsx";
// @ts-ignore
import SummaryBox from "../MainPage/Charts/SummaryBox/SummaryBox.tsx";
// @ts-ignore
import Charts from "../MainPage/Charts/Charts.tsx";
// @ts-ignore
import dayjs from "dayjs";
// @ts-ignore
import Title from "../Title/Title.tsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
// @ts-ignore
import Pagination from "../Pagination/Pagination.tsx";
// @ts-ignore
import { api } from "../../api/client.tsx";
// @ts-ignore
import { Loader } from "../../Loading/Loading.tsx";
// @ts-ignore
import Breadcrumbs from "../BreadcrumbNav/Breadcrumbs.tsx";

type Repo = {
  name: string;
  is_official: boolean;
  last_updated: string | null;
  category: string[];
  description?: string;
  versions?: number;
  pull_count?: number;
};

type Filters = {
  is_official: ("All" | boolean)[];
  last_updated: { from: string | null; to: string | null };
  versions: [number, number];
  category: string[];
};

type Paged<T> = { items: T[]; total: number; page: number; page_size: number };

type OverviewStats = {
  total_repos: number;
  total_images: number;
  total_vulnerabilities: number;
  avg_vulns_per_image: number;
};

type PercentagesMap = Record<string, number>;

type Level0Highlights = {
  top_popular: any[];
  top_most_versions: any[];
  top_recently_updated: any[];
};

type RepoStat = {
  name: string;
  count?: number;
  size?: string;
  time?: string;
};

function isAbortError(err: any): boolean {
  if (!err) return false;
  const name = String(err.name || "").toLowerCase();
  const msg = String(err.message || "").toLowerCase();
  return (
    name === "aborterror" ||
    msg.includes("aborted") ||
    msg.includes("canceled") ||
    msg.includes("cancelled")
  );
}

const parseDay = (v: unknown) => {
  if (v == null) return null;
  let d = dayjs(v as any);
  if (d.isValid()) return d;
  const n = Number(v);
  if (Number.isFinite(n)) {
    d = dayjs(n > 1e12 ? n : n * 1000);
    if (d.isValid()) return d;
  }
  return null;
};

const safeISO = (v: unknown): string | undefined => {
  const d = parseDay(v);
  return d ? d.toDate().toISOString() : undefined;
};

const safeFormat = (v: unknown, fmt = "YYYY-MM-DD"): string => {
  const d = parseDay(v);
  return d ? d.format(fmt) : "—";
};

function normalizeRow(img: any): Repo | null {
  const name =
    img?.name ??
    img?.Name ??
    img?.image ??
    img?.Image ??
    img?.repository ??
    img?.Repository ??
    "";

  if (!name) return null;

  const rawCats =
    img?.categories ?? img?.Categories ?? img?.category ?? img?.Category ?? [];

  const catSet = new Set<string>();
  const push = (s: unknown) => {
    if (s == null) return;
    const raw = String(s).trim();
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw.replace(/'/g, '"'));
      if (Array.isArray(parsed)) {
        parsed.forEach((v) => push(v));
        return;
      }
      if (typeof parsed === "string") {
        raw.split(",").forEach((p) => {
          const v = p.trim();
          if (v) catSet.add(v);
        });
        return;
      }
    } catch {}
    raw.split(",").forEach((p) => {
      const v = p.trim();
      if (v) catSet.add(v);
    });
  };

  if (Array.isArray(rawCats)) {
    rawCats.forEach((item) =>
      Array.isArray(item) ? item.forEach(push) : push(item)
    );
  } else {
    push(rawCats);
  }

  const last_updated =
    img?.last_updated ?? img?.Last_Updated ?? img?.updated_at ?? null;
  const is_official = Boolean(
    img?.is_official ?? img?.Is_Official ?? img?.official
  );
  const description: string | undefined =
    (img?.description ??
      img?.Description ??
      img?.desc ??
      img?.summary ??
      img?.short_description) || undefined;

  const versions =
    img?.versions !== undefined ? Number(img.versions) : undefined;
  const pull_count =
    img?.pull_count !== undefined ? Number(img.pull_count) : undefined;

  return {
    name,
    is_official,
    last_updated,
    category: Array.from(catSet),
    description,
    versions: Number.isFinite(versions) ? versions : undefined,
    pull_count: Number.isFinite(pull_count) ? pull_count : undefined,
  };
}

function QMark({ label, tip }: { label: string; tip: string }) {
  return (
    <button
      type="button"
      className="qmark"
      aria-label={label}
      data-tip={tip}
      onClick={(e) => e.preventDefault()}
    >
      <span aria-hidden>?</span>
    </button>
  );
}

const COL_DESC = {
  repo:
    "A collection of versioned container images of the same software. Click a row to open its versions/tags. You can search by repository name using the search bar above.",
  versions:
    "Number of container image versions/tags contained in the repository. Controlled by the Versions slider filter.",
  status:
    "Maintainer type for this repository: 'Official' means maintained by the vendor; 'Community' means maintained by external contributors.",
  updated:
    "Timestamp of the most recent version indexed for this repository, shown as YYYY-MM-DD. Controlled by the Last Updated date range filter.",
  pulls:
    "Number of times the repository has been downloaded/pulled from the registry.",
  category:
    "Software type for the repository (e.g., Database, Machine Learning, Security, Networking). One repository can belong to multiple categories.",
  desc: "Brief description of the repository’s function as provided by the registry.",
};


export default function MainPage() {
  const navigate = useNavigate();

  const [pageItems, setPageItems] = useState<Repo[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");

  const [filters, setFilters] = useState<Filters>({
    is_official: ["All"],
    last_updated: { from: null, to: null },
    versions: [0, 0],
    category: ["All"],
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [overviewStats, setOverviewStats] = useState<OverviewStats | null>(
    null
  );
  const [percentages, setPercentages] = useState<PercentagesMap | null>(null);
  const [highlights, setHighlights] = useState<Level0Highlights | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  useEffect(() => {
    let aborted = false;

    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const payload = await api.level0.listPaged(currentPage, itemsPerPage);
        if (aborted) return;

        if (!payload || !Array.isArray((payload as Paged<any>).items)) {
          throw new Error("Unexpected payload shape from /level0");
        }
        const normalized = (payload as Paged<any>).items
          .map(normalizeRow)
          .filter((x): x is Repo => Boolean(x));

        setPageItems(normalized);
        setTotal((payload as Paged<any>).total);
      } catch (err: any) {
        if (aborted || isAbortError(err)) return;
        console.error("Failed to load /level0:", err);
        setLoadError(
          typeof err?.message === "string"
            ? err.message
            : "Failed to load images"
        );
        setPageItems([]);
        setTotal(0);
      } finally {
        if (!aborted) setLoading(false);
      }
    })();

    return () => {
      aborted = true;
    };
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    let ignore = false;

    (async () => {
      setStatsLoading(true);
      setStatsError(null);
      try {
        const bundle = await api.stats.homeBundle();
        if (ignore) return;

        // @ts-ignore
        setOverviewStats(bundle.overview ?? null);
        setPercentages(bundle.percentages ?? {});
        setHighlights(bundle.level0Highlights);
        setStatsError(null);
      } catch (e: any) {
        if (ignore || isAbortError(e)) return;
        console.error("Failed to load home bundle:", e);
        setStatsError(e?.message ?? "Failed to load dashboard statistics");
        setOverviewStats(null);
        setPercentages(null);
        setHighlights(null);
      } finally {
        if (!ignore) setStatsLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, []);

  const versionsMax = useMemo(
    () =>
      Math.max(
        0,
        ...pageItems.map((r) =>
          Number.isFinite(r.versions) ? (r.versions as number) : 0
        )
      ),
    [pageItems]
  );

  useEffect(() => {
    setFilters((prev) => {
      const cur = prev.versions;
      if (versionsMax > 0 && cur[0] === 0 && cur[1] === 0) {
        return { ...prev, versions: [0, versionsMax] };
      }
      const lo = Math.max(0, Math.min(prev.versions[0], versionsMax));
      const hi = Math.max(0, Math.min(prev.versions[1], versionsMax));
      if (lo !== prev.versions[0] || hi !== prev.versions[1]) {
        return { ...prev, versions: [lo, hi] };
      }
      return prev;
    });
  }, [versionsMax]);

  const filteredRepos = useMemo(() => {
    const term = activeSearchTerm.trim().toLowerCase();

    return pageItems.filter((repo) => {
      const officialSel = filters.is_official[0];
      const matchesOfficial =
        officialSel === "All" || officialSel === undefined
          ? true
          : (officialSel === true && repo.is_official === true) ||
            (officialSel === false && repo.is_official === false);

      const from = parseDay(filters.last_updated.from);
      const to = parseDay(filters.last_updated.to);
      const lastUpdated = parseDay(repo.last_updated);
      const matchesDate =
        (!from || (lastUpdated && lastUpdated.isAfter(from))) &&
        (!to || (lastUpdated && lastUpdated.isBefore(to)));

      const selectedCategory = (filters.category[0] || "").trim();
      const matchesCategory =
        !selectedCategory || selectedCategory === "All"
          ? true
          : Array.isArray(repo.category) &&
            repo.category.some(
              (c) =>
                c.trim().toLowerCase() === selectedCategory.toLowerCase()
            );

      const [vmin, vmax] = filters.versions;
      const v = Number.isFinite(repo.versions)
        ? (repo.versions as number)
        : 0;
      const versionsEnabled = vmax > 0 && vmin <= vmax;
      const matchesVersions = !versionsEnabled ? true : v >= vmin && v <= vmax;

      if (
        !matchesOfficial ||
        !matchesDate ||
        !matchesCategory ||
        !matchesVersions
      ) {
        return false;
      }

      if (!term) return true;

      const statusText = repo.is_official ? "official" : "community";
      const categoryText = Array.isArray(repo.category)
        ? repo.category.join(" ")
        : "";
      const dateText = safeFormat(repo.last_updated, "YYYY-MM-DD");
      const pullsText = Number.isFinite(repo.pull_count)
        ? String(repo.pull_count)
        : "";
      const versionsText = Number.isFinite(repo.versions)
        ? String(repo.versions)
        : "";
      const desc = (repo.description || "").trim();

      const haystack = [
        repo.name,
        statusText,
        categoryText,
        dateText,
        pullsText,
        versionsText,
        desc,
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [pageItems, filters, activeSearchTerm]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / itemsPerPage)),
    [total, itemsPerPage]
  );

  const mostPopular: RepoStat[] = useMemo(() => {
    if (!highlights) return [];
    return (highlights.top_popular || []).map((x: any) => ({
      name: x.name,
      count: Number(x.pull_count ?? 0),
    }));
  }, [highlights]);

  const mostVersions: RepoStat[] = useMemo(() => {
    if (!highlights) return [];
    return (highlights.top_most_versions || []).map((x: any) => ({
      name: x.name,
      count: Number(x.versions ?? 0),
    }));
  }, [highlights]);

  const recentlyUpdated: RepoStat[] = useMemo(() => {
    if (!highlights) return [];
    return (highlights.top_recently_updated || []).map((x: any) => ({
      name: x.name,
      time: x.last_updated,
    }));
  }, [highlights]);

  const handleRowClick = (repoName: string) => {
    const cleanName = repoName.replace(/\//g, "_");
    navigate(`/versions/${cleanName}`);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setActiveSearchTerm(searchTerm);
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };


  // @ts-ignore
  return (
    <div className="main-container">
      <header className="l0-header">
        <h1 className="l0-title">
          <Title />
          <p className="p-warning">
            Due to the large volume of data being processed, loading may take a
            few moments. Thank you for your patience.
          </p>
        </h1>
      </header>

      <div className="l0-header-divider" aria-hidden="true" />

      <div className="info-btn-div">
        <button
          className="side-info-btn"
          onClick={() => navigate("/info")}
          aria-label="Open Information"
          title="Information"
        >
          <FontAwesomeIcon icon={faCircleInfo} />
          <span className="side-info-label">Info</span>
        </button>
      </div>

      <div className="breadcrumb-mainpage-div">
        <Breadcrumbs items={[{ label: "Home", to: "/" }]} />
      </div>

      {/* TOP DASHBOARD PANELS */}
      <section className="top-panels">
        <div className="panel-card panel-left">
          <TopCharts
            loading={statsLoading}
            error={statsError}
            mostPopular={mostPopular}
            mostVersions={mostVersions}
            recentlyUpdated={recentlyUpdated}
          />
        </div>

        <div className="panel-right">
          <div className="panel-card">
            <h2 className="panel-title">Visualizations</h2>
            <Charts percentages={percentages} />
          </div>
          <div className="panel-card">
            <h2 className="panel-title">
              Summary of Container Repositories (A Small Excerpt)
            </h2>
            <SummaryBox
              stats={overviewStats}
              loading={statsLoading}
              error={statsError}
            />
          </div>
        </div>
      </section>

      {/* FILTERS + TABLE */}
      <section className="bottom-panels">
        <aside className="panel-card filter-card">
          <h2 className="panel-title">Filters</h2>

          <div className="controls">
            <input
              className="search-box"
              type="text"
              placeholder="Search by any value in the table…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <button
              className="btn btn-primary"
              onClick={handleSearch}
              aria-label="Search repositories"
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              <span>Search</span>
            </button>
          </div>

          <FilterPanel
            filters={filters}
            setFilters={setFilters}
            versionsMax={versionsMax}
          />
        </aside>

        <div className="panel-card table-card">
          <div className="table-titlebar" role="heading" aria-level={2}>
            <span className="table-title">List of Container Repositories</span>
          </div>

          <div className="scrollable-table-container">
            <table className="mainContainer-table" style={{ minWidth: 980 }}>
              <colgroup>
                <col style={{ width: "22%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "24%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "14%" }} />
              </colgroup>

              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  background: "#edf7e6",
                }}
              >
                <tr>
                  <th className="th-with-q">
                    <span className="th-title">Repository Name</span>
                    <QMark label="Repository Name help" tip={COL_DESC.repo} />
                  </th>
                  <th className="th-with-q">
                    <span className="th-title">Versions</span>
                    <QMark label="Versions help" tip={COL_DESC.versions} />
                  </th>
                  <th className="th-with-q">
                    <span className="th-title">Pull Count</span>
                    <QMark label="Pull Count help" tip={COL_DESC.pulls} />
                  </th>
                  <th className="th-with-q">
                    <span className="th-title">Status</span>
                    <QMark label="Status help" tip={COL_DESC.status} />
                  </th>
                  <th className="th-with-q">
                    <span className="th-title">Category</span>
                    <QMark label="Category help" tip={COL_DESC.category} />
                  </th>
                  <th className="th-with-q">
                    <span className="th-title">Last Updated</span>
                    <QMark label="Last Updated help" tip={COL_DESC.updated} />
                  </th>
                  <th className="th-with-q">
                    <span className="th-title">Description</span>
                    <QMark label="Description help" tip={COL_DESC.desc} />
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} style={{ padding: 16 }}>
                      <Loader />
                    </td>
                  </tr>
                ) : loadError ? (
                  <tr>
                    <td colSpan={7} style={{ padding: 16, color: "crimson" }}>
                      Failed to load repositories: {loadError}
                    </td>
                  </tr>
                ) : filteredRepos.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: 16 }}>
                      No repositories found.
                    </td>
                  </tr>
                ) : (
                  filteredRepos.map((repo) => {
                    const categoryList =
                      Array.isArray(repo.category) &&
                      repo.category.length > 0
                        ? repo.category.join(", ")
                        : "";
                    const dateText = safeFormat(
                      repo.last_updated,
                      "YYYY-MM-DD"
                    );
                    const iso = safeISO(repo.last_updated);
                    const desc = (repo.description || "").trim();

                    return (
                      <tr
                        key={repo.name}
                        onClick={() => handleRowClick(repo.name)}
                        style={{ cursor: "pointer" }}
                      >
                        <td>{repo.name}</td>
                        <td>
                          {Number.isFinite(repo.versions)
                            ? repo.versions
                            : "—"}
                        </td>
                        <td>
                          {Number.isFinite(repo.pull_count)
                            ? repo.pull_count!.toLocaleString()
                            : "—"}
                        </td>
                        <td>
                          <span
                            className={`tag ${
                              repo.is_official
                                ? "tag--official"
                                : "tag--community"
                            }`}
                          >
                            {repo.is_official ? "Official" : "Community"}
                          </span>
                        </td>
                        <td className="category-list">{categoryList || ""}</td>
                        <td>
                          {iso ? (
                            <time dateTime={iso}>{dateText}</time>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="description-cell" title={desc || ""}>
                          {desc || "—"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-controls">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={goToPage}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
